import SelectObjectAsValue from '@/src/components/SelectObjectAsValue';
import useResetFormOnCloseModal from '@/src/hooks/useResetFormOnCloseModal';
import { Form, Modal, Popover } from 'antd';
import React, { useEffect } from 'react';
import styles from '../style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, hasCircleValid } from 'src/tools/tools';
import { Icon } from '@/src/tools/icon';
import { Label, TextArea } from '@/src/components/Antd';
import {
  getTranslationModeOptions,
  translationPowerOptions,
} from '../constants';

const TranslationSettingModal = ({
  visible,
  attrIndex,
  onCancel,
  onModalConfirm,
  availableLanguages,
  languageOptionsList,
}: {
  visible: boolean;
  attrIndex: number;
  attrData: any;
  onCancel: () => void;
  onModalConfirm: (index: number, data: any) => void;
  availableLanguages: any[];
  languageOptionsList: any[];
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [form] = Form.useForm();
  useResetFormOnCloseModal({
    form,
    visible,
  });

  const getOptions = (languageOptionsList, excludeTag?) => {
    return languageOptionsList
      .filter((o) => {
        if (!excludeTag) return true;
        const tag = o.languageTag || o.tag;
        return tag !== excludeTag;
      })
      .map((o) => ({
        ...o,
        label: o.description,
        value: o.languageTag || o.tag,
      }));
  };
  function getOptionByTag(languages, tag) {
    const found = languages.find((o) => o.value === tag || o.tag === tag);
    return found
      ? {
          label: found.label || found.description,
          value: found.value || found.tag,
        }
      : undefined;
  }

  const currentLanguageTag =
    availableLanguages[attrIndex]?.tag ||
    availableLanguages[attrIndex]?.languageTag;
  const sourceLanguageOptions = getOptions(
    availableLanguages,
    currentLanguageTag,
  );
  const targetLanguageOptions = getOptions(languageOptionsList);

  const translationModeOptions = getTranslationModeOptions(getI18n);

  useEffect(() => {
    if (visible) {
      const {
        additionalRequirement: _serverAdditionalRequirement,
        sourceLanguage: _serverSourceLanguage,
        targetLanguage: _serverTargetLanguage,
        translationMode: _serverTranslationMode,
      } = availableLanguages[attrIndex]?.translationSetting || {};

      const source =
        getOptionByTag(sourceLanguageOptions, _serverSourceLanguage) ||
        sourceLanguageOptions.find((opt) => opt.primary);

      const target =
        getOptionByTag(targetLanguageOptions, _serverTargetLanguage) ||
        targetLanguageOptions[attrIndex];

      form.setFieldsValue({
        sourceLanguage: source,
        targetLanguage: target,
        translationMode:
          translationModeOptions.find(
            (o) => o.value === _serverTranslationMode,
          ) || translationModeOptions[0],
        translationPower: translationPowerOptions[0],
        additionalRequirement: _serverAdditionalRequirement,
      });
    }
  }, [
    visible,
    attrIndex,
    availableLanguages,
    sourceLanguageOptions,
    targetLanguageOptions,
    translationModeOptions,
  ]);

  const onOk = () => {
    form
      .validateFields()
      .then((res) => {
        onModalConfirm(attrIndex, res);
      })
      .catch((e) => {
        console.log(e);
      });
  };
  const Hint = () => {
    const i18n = useI18n();
    const getI18n = (str) => i18nGet(i18n, str);
    return (
      <div className={styles.hint}>
        <div className={styles.paragraph}>
          {getI18n('salesChannelLanguage.index.translationTip1')}
          <br />
          {getI18n('salesChannelLanguage.index.translationTip2')}
          <br />
          {getI18n('salesChannelLanguage.index.translationTip3')}
          <br />
          {getI18n('salesChannelLanguage.index.translationTip4')}
        </div>
      </div>
    );
  };
  return (
    <Modal
      forceRender={true}
      title={
        <div>
          <span>
            {' '}
            {getI18n('salesChannelLanguage.index.translationSetting')}
          </span>
          <Popover
            title={null}
            content={<Hint />}
            placement='rightTop'
            overlayStyle={{ maxWidth: 310, whiteSpace: 'normal' }}>
            <Icon type='icontooltip' style={{ marginLeft: '12px' }} />
          </Popover>
        </div>
      }
      visible={visible}
      okText={getI18n('general.confirm')}
      cancelText={getI18n('general.cancel')}
      maskClosable={false}
      width={600}
      onOk={onOk}
      onCancel={onCancel}>
      <Form
        form={form}
        layout='vertical'
        name='translationSetting'
        className={styles.translateModalContainer}>
        <Form.Item
          name='sourceLanguage'
          className={styles.select}
          label={
            <div className={styles.selectLabel}>
              <div className={styles.selectLabelTitle}>
                {getI18n('salesChannelLanguage.index.sourcelanguage')}*
              </div>
            </div>
          }
          rules={[
            { required: true, message: 'Please select source language' },
            {
              validator: (_, value) => {
                const curTag = availableLanguages[attrIndex].tag;
                const nextSourceTag = value?.value;

                if (hasCircleValid(availableLanguages, curTag, nextSourceTag)) {
                  return Promise.reject(
                    getI18n('salesChannelLanguage.index.circleValid'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}>
          <SelectObjectAsValue
            objectAsValue
            options={sourceLanguageOptions}
            size='large'
          />
        </Form.Item>
        <Form.Item
          name='targetLanguage'
          className={styles.select}
          label={
            <div className={styles.selectLabel}>
              <div className={styles.selectLabelTitle}>
                {getI18n('salesChannelLanguage.index.targetlanguage')}*
              </div>
            </div>
          }
          rules={[{ required: true }]}>
          <SelectObjectAsValue
            objectAsValue
            options={targetLanguageOptions}
            size='large'
          />
        </Form.Item>
        <Form.Item
          name='translationMode'
          className={styles.select}
          label={
            <div className={styles.selectLabel}>
              <div className={styles.selectLabelTitle}>
                {getI18n('salesChannelLanguage.index.translationmode')}*
              </div>
            </div>
          }
          rules={[{ required: true }]}>
          <SelectObjectAsValue
            objectAsValue
            options={translationModeOptions}
            size='large'
          />
        </Form.Item>
        <Form.Item
          name='translationPower'
          className={styles.select}
          label={
            <div className={styles.selectLabel}>
              <div className={styles.selectLabelTitle}>
                {getI18n('salesChannelLanguage.index.poweredby')}*
              </div>
            </div>
          }
          rules={[{ required: true }]}>
          <SelectObjectAsValue
            objectAsValue
            disabled
            options={translationPowerOptions}
            size='large'
          />
        </Form.Item>
        <Form.Item
          name='additionalRequirement'
          className={styles.additionalRequirement}
          label={
            <Label
              title={`${getI18n(
                'salesChannelLanguage.index.additionalRequirement',
              )}`}
              textLength={5000}
              nameKey='additionalRequirement'
            />
          }>
          <TextArea
            size='large'
            maxLength={5000}
            placeholder={getI18n(
              'salesChannelLanguage.index.additionalRequirementInfo',
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TranslationSettingModal;
